/**
 * Onglet Twitch CLI pour l'administration
 * @module admin/tabs/TwitchCliTab
 */

import React, { useEffect } from 'react';
import { useTwitchCli } from '../hooks/useTwitchCli';
import AdminStatus from '../components/AdminStatus';
import AdminResult from '../components/AdminResult';
import EventFields from '../components/EventFields';

/**
 * Composant onglet Twitch CLI
 * @returns {JSX.Element}
 */
const TwitchCliTab = () => {
  const {
    cliStatus,
    availableEvents,
    selectedEvent,
    eventOptions,
    isLoading,
    result,
    error,
    history,
    isCliReady,
    fetchCliStatus,
    fetchAvailableEvents,
    handleEventChange,
    handleOptionChange,
    handleTriggerEvent,
    replayEvent,
    deleteHistoryEntry,
    clearHistory,
  } = useTwitchCli();

  useEffect(() => {
    fetchCliStatus();
    fetchAvailableEvents();
  }, [fetchCliStatus, fetchAvailableEvents]);

  const handleEventSelectChange = (e) => {
    handleEventChange(e.target.value);
  };

  return (
    <>
      <section className="admin-section">
        <h2>📡 Statut du Twitch CLI</h2>
        {cliStatus === null ? (
          <p className="admin-loading">Chargement...</p>
        ) : (
          <AdminStatus
            isOk={isCliReady}
            message={
              isCliReady
                ? `Twitch CLI installé - ${cliStatus.version}`
                : cliStatus.error
            }
          />
        )}
      </section>

      <section className="admin-section">
        <h2>🎯 Simuler un événement EventSub</h2>

        <div className="admin-form">
          <div className="admin-field">
            <label htmlFor="eventType">Type d'événement</label>
            <select
              id="eventType"
              value={selectedEvent}
              onChange={handleEventSelectChange}
              disabled={!isCliReady}
            >
              <option value="">Sélectionner un événement...</option>
              {availableEvents.map((event) => (
                <option key={event.type} value={event.type}>
                  {event.type}
                </option>
              ))}
            </select>
          </div>

          {selectedEvent && (
            <EventFields
              selectedEvent={selectedEvent}
              eventOptions={eventOptions}
              onOptionChange={handleOptionChange}
            />
          )}

          <button
            className="admin-button admin-button-primary"
            onClick={handleTriggerEvent}
            disabled={isLoading || !selectedEvent || !isCliReady}
          >
            {isLoading ? 'Exécution...' : "Déclencher l'événement"}
          </button>
        </div>

        {result && (
          <AdminResult isSuccess={true} title="Résultat">
            <p>
              <strong>Message:</strong> {result.message}
            </p>
            <p>
              <strong>Commande:</strong> <code>{result.command}</code>
            </p>
            {result.output && (
              <div className="admin-output">
                <strong>Sortie:</strong>
                <pre>{result.output}</pre>
              </div>
            )}
            {result.warnings && (
              <div className="admin-warnings">
                <strong>Avertissements:</strong>
                <pre>{result.warnings}</pre>
              </div>
            )}
          </AdminResult>
        )}

        {error && (
          <AdminResult isSuccess={false} title="Erreur" message={error} />
        )}
      </section>

      <section className="admin-section">
        <div className="admin-section-header">
          <h2>📜 Historique des événements simulés</h2>
          {history.length > 0 && (
            <button
              className="admin-button admin-button-secondary"
              onClick={clearHistory}
            >
              Effacer l'historique
            </button>
          )}
        </div>

        {history.length === 0 ? (
          <p className="admin-info">Aucun événement dans l'historique.</p>
        ) : (
          <div className="admin-history">
            {history.map((entry) => (
              <div key={entry.id} className="admin-history-entry">
                <div className="admin-history-header">
                  <span className="admin-history-event">
                    {entry.eventType}
                  </span>
                  <span className="admin-history-time">
                    {new Date(entry.timestamp).toLocaleString('fr-FR')}
                  </span>
                </div>
                <div className="admin-history-options">
                  {Object.keys(entry.options).length > 0 ? (
                    <code>{JSON.stringify(entry.options)}</code>
                  ) : (
                    <em>Aucune option</em>
                  )}
                </div>
                <div className="admin-history-actions">
                  <button
                    className="admin-button admin-button-small"
                    onClick={() => replayEvent(entry)}
                  >
                    Rejouer
                  </button>
                  <button
                    className="admin-button admin-button-small admin-button-danger"
                    onClick={() => deleteHistoryEntry(entry.id)}
                  >
                    Supprimer
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </section>
    </>
  );
};

export default TwitchCliTab;
